/**
 * Global project run status (0–7) from gateway `projectRunStatus` only.
 * Every UI surface must read via readProjectRunStatus — never infer from legacy fields alone.
 */

import type { ExecutionStatus, ProjectExecute } from "../types/ead-types.ts";

export const ProjectRunStatus = {
  Planned: 0,
  Initializing: 1,
  Running: 2,
  UserStopDone: 3,
  UserStopCancel: 4,
  AiStopDone: 5,
  AiStopCancel: 6,
  Abandoned: 7,
} as const;

export type ProjectRunStatus = (typeof ProjectRunStatus)[keyof typeof ProjectRunStatus];

const MAX_STATUS = ProjectRunStatus.Abandoned;

const LABELS: Record<ProjectRunStatus, string> = {
  [ProjectRunStatus.Planned]: "Planned",
  [ProjectRunStatus.Initializing]: "Initializing...",
  [ProjectRunStatus.Running]: "Running...",
  [ProjectRunStatus.UserStopDone]: "User Stop - Done",
  [ProjectRunStatus.UserStopCancel]: "User Stop - Cancel",
  [ProjectRunStatus.AiStopDone]: "AI Finish",
  [ProjectRunStatus.AiStopCancel]: "AI Failed",
  [ProjectRunStatus.Abandoned]: "Abandoned",
};

type StatusCarrier = {
  projectRunStatus?: number;
  project_run_status?: number;
  status?: string;
  abandoned?: boolean | null;
  operatorPhase?: string;
  operator_phase?: string;
  operatorStopKind?: string;
  operator_stop_kind?: string;
  executorHint?: string;
  executor_hint?: string;
};

/** Raw executor `projectRunStatus` (0–7). Undefined when the field is absent. */
export function pickPrsRaw(ex: StatusCarrier): number | undefined {
  const raw = ex.projectRunStatus ?? ex.project_run_status;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const n = Math.trunc(raw);
    if (n >= ProjectRunStatus.Planned && n <= MAX_STATUS) return n;
  }
  if (typeof raw === "string" && raw.trim() && Number.isFinite(Number(raw))) {
    const n = Math.trunc(Number(raw));
    if (n >= ProjectRunStatus.Planned && n <= MAX_STATUS) return n;
  }
  return undefined;
}

/** True when global status is terminal (finished/closed), not Initializing or Running. */
export function isTerminalProjectRunStatus(status: ProjectRunStatus): boolean {
  return status >= ProjectRunStatus.UserStopDone;
}

/** Executor-written PRS only — never inferred from chat, logs, or timestamps. */
export function storedProjectRunStatus(ex: StatusCarrier): ProjectRunStatus | undefined {
  const raw = pickPrsRaw(ex);
  if (raw === undefined) return undefined;
  return raw as ProjectRunStatus;
}

/**
 * Legacy fallback for closed/historical rows that predate numeric `projectRunStatus`.
 * Do not use for live-run phase gates — executor PRS is authoritative when present.
 */
function readLegacyProjectRunStatusFallback(ex: StatusCarrier): ProjectRunStatus {
  const status = String(ex.status || "").toLowerCase();
  if (status === "pending") return ProjectRunStatus.Initializing;
  if (status === "initializing") return ProjectRunStatus.Initializing;
  if (status === "running") return ProjectRunStatus.Running;
  if (status === "completed") return ProjectRunStatus.AiStopDone; // Terminal - AI Finish
  if (status === "cancelled") return ProjectRunStatus.UserStopCancel; // Terminal
  if (status === "failed" || status === "error") return ProjectRunStatus.AiStopCancel; // Terminal
  return ProjectRunStatus.Initializing;
}

/** Read canonical status — executor `projectRunStatus` wins when present. */
export function readProjectRunStatus(ex: StatusCarrier): ProjectRunStatus {
  if (ex.abandoned === true) return ProjectRunStatus.Abandoned;
  const status = String(ex.status || "").toLowerCase();
  if (status === "abandoned") return ProjectRunStatus.Abandoned;

  const stored = storedProjectRunStatus(ex);
  if (stored !== undefined) return stored;

  return readLegacyProjectRunStatusFallback(ex);
}

function legacyStatusForPrs(prs: ProjectRunStatus): ExecutionStatus {
  switch (prs) {
    case ProjectRunStatus.Planned:
      return "pending";
    case ProjectRunStatus.Running:
      return "running";
    case ProjectRunStatus.UserStopDone:
    case ProjectRunStatus.AiStopDone:
      return "completed";
    case ProjectRunStatus.UserStopCancel:
    case ProjectRunStatus.AiStopCancel:
      return "cancelled";
    case ProjectRunStatus.Abandoned:
      return "abandoned";
    default:
      return "initializing";
  }
}

function operatorPhaseForPrs(prs: ProjectRunStatus): string {
  if (prs === ProjectRunStatus.Running) return "Running";
  if (isTerminalProjectRunStatus(prs)) return "Run complete";
  if (prs === ProjectRunStatus.Planned) return "Initializing";
  return "Initializing";
}

function executorHintForPrs(prs: ProjectRunStatus): string {
  return LABELS[prs]?.replace(/\.\.\.$/, "") ?? "Initializing";
}

/**
 * Align legacy display fields from canonical PRS so status/phase/hint never disagree with global state.
 */
export function syncCanonicalRunFields<T extends StatusCarrier>(ex: T): T {
  const prs = readProjectRunStatus(ex);
  const out = ex as T & ProjectExecute;
  // Preserve executor-written PRS when already on the row; only fill when missing.
  if (storedProjectRunStatus(ex) === undefined) {
    out.projectRunStatus = prs as ProjectExecute["projectRunStatus"];
  }

  out.status = legacyStatusForPrs(prs);
  out.operatorPhase = operatorPhaseForPrs(prs);

  if (prs === ProjectRunStatus.UserStopDone) {
    out.operatorStopKind = out.operatorStopKind ?? "finish";
  } else if (prs === ProjectRunStatus.UserStopCancel) {
    out.operatorStopKind = out.operatorStopKind ?? "cancel";
  } else if (isTerminalProjectRunStatus(prs)) {
    out.operatorStopKind = undefined;
  }

  if (prs === ProjectRunStatus.Abandoned) {
    out.abandoned = true;
  }

  const hint = executorHintForPrs(prs);
  if (isTerminalProjectRunStatus(prs) || prs === ProjectRunStatus.Running) {
    out.executorHint = hint;
  } else if (!String(out.executorHint || "").trim()) {
    out.executorHint = hint;
  }

  if (isTerminalProjectRunStatus(prs)) {
    out.timeBudgetExceeded = false;
    if (prs !== ProjectRunStatus.Running) {
      out.bootstrapPending = false;
    }
  }

  return out;
}

export function projectRunStatusLabel(status: ProjectRunStatus): string {
  return LABELS[status] ?? "Initializing...";
}

export function projectRunStatusLabelForExecution(
  ex: Parameters<typeof readProjectRunStatus>[0],
): string {
  return projectRunStatusLabel(readProjectRunStatus(ex));
}

/** Sidebar dot class from canonical PRS. */
export function projectRunStatusSidebarClass(ex: StatusCarrier): string {
  const prs = readProjectRunStatus(ex);
  if (prs === ProjectRunStatus.Initializing || prs === ProjectRunStatus.Running) {
    return "sidebar-run__status--active";
  }
  if (prs === ProjectRunStatus.AiStopCancel) return "sidebar-run__status--failed";
  return "sidebar-run__status--finished";
}

export function isLiveProjectRunStatus(status: ProjectRunStatus): boolean {
  return (
    status === ProjectRunStatus.Initializing || status === ProjectRunStatus.Running
  );
}

export function isLiveProjectExecute(ex: Parameters<typeof readProjectRunStatus>[0]): boolean {
  const status = readProjectRunStatus(ex);

  // Explicitly check for terminal legacy status strings that should not be treated as live
  const legacyStatus = String(ex.status || "").toLowerCase();
  if (legacyStatus === "completed" || legacyStatus === "cancelled" || legacyStatus === "failed" || legacyStatus === "error") {
    return false;
  }

  return isLiveProjectRunStatus(status);
}

export function isExplorationProjectRunStatus(status: ProjectRunStatus): boolean {
  return status === ProjectRunStatus.Running;
}

export function isLoginPhaseCompleteStatus(status: ProjectRunStatus): boolean {
  return status >= ProjectRunStatus.Running;
}

/**
 * Merge executor PRS from a poll payload without regressing Running → Initializing.
 * Terminal statuses from fresh always win; among live phases use the higher ordinal.
 */
export function mergeStoredProjectRunStatus(
  base: StatusCarrier,
  fresh: StatusCarrier,
): ProjectRunStatus | undefined {
  const freshStored = storedProjectRunStatus(fresh);
  const baseStored = storedProjectRunStatus(base);
  if (freshStored === undefined) return baseStored;
  if (baseStored === undefined) return freshStored;
  if (isTerminalProjectRunStatus(freshStored)) return freshStored;
  if (isTerminalProjectRunStatus(baseStored)) return baseStored;
  const higher = Math.max(baseStored, freshStored);
  return higher as ProjectRunStatus;
}
